import { ApplicationConfig, isDevMode, provideAppInitializer, provideBrowserGlobalErrorListeners, inject } from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { provideServiceWorker } from '@angular/service-worker';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalInterceptor,
  MsalService,
} from '@azure/msal-angular';

import { routes } from './app.routes';
import { msalGuardConfigFactory, msalInstanceFactory, msalInterceptorConfigFactory } from './auth/msal.config';
import { AuthService } from './auth/auth.service';
import { assertEnvironmentConfigured } from '../environments/environment-check';
import { environment } from '../environments/environment';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withInterceptorsFromDi()),

    // MSAL. Instance/guard/broadcast stay registered even in e2e (AuthService and
    // the shell inject MsalService), but e2e skips the token interceptor and the
    // MSAL bootstrap below — the API runs the test-auth scheme, so there's no real
    // login or bearer token in play.
    { provide: MSAL_INSTANCE, useFactory: msalInstanceFactory },
    { provide: MSAL_GUARD_CONFIG, useFactory: msalGuardConfigFactory },
    { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: msalInterceptorConfigFactory },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    ...(environment.e2e
      ? []
      : [{ provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true }]),

    // App initializer: validate config + wire MSAL. The realtime hub is started reactively
    // by AuthService when isAuthenticated() flips to true (handles fresh-login redirects too).
    provideAppInitializer(async () => {
      if (environment.e2e) return;
      assertEnvironmentConfigured();
      const msal = inject(MsalService);
      const auth = inject(AuthService);

      await msal.instance.initialize();
      await msal.instance.handleRedirectPromise().catch(() => null);
      auth.initialize();
    }),

    // PWA service worker. Only registered in production builds (Angular SW disabled in dev
    // because it caches aggressively and gets in the way of fast iteration).
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
